'use client';

import { useCallback, useEffect, useState } from 'react';
import { T, useTranslation } from '@/i18n/LocaleProvider';
import Box from '@mui/material/Box';
import Switch from '@mui/material/Switch';
import Typography from '@mui/material/Typography';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { MONO_FONT } from '@/theme';
import Button from './Button';
import { CONSENT_OPEN_EVENT, readConsent, writeConsent } from '@/libs/consent';

/* Consent Mode v2 (modo básico): GTM — y con él GA4, Clarity y Meta Pixel — no se
 * descarga hasta que el visitante acepta al menos una categoría. Por defecto todo
 * queda en 'denied'. */

const DENIED = {
  ad_storage: 'denied',
  ad_user_data: 'denied',
  ad_personalization: 'denied',
  analytics_storage: 'denied',
};

// Cookies de terceros por categoría, para limpiarlas al retirar el consentimiento.
const ANALYTICS_COOKIES = [
  /^_ga/,
  /^_gid$/,
  /^_gat/,
  /^_clck$/,
  /^_clsk$/,
  /^CLID$/,
  /^MUID$/,
  /^ANONCID$/,
];
const MARKETING_COOKIES = [/^_fbp$/, /^_fbc$/];

function gtag() {
  window.dataLayer.push(arguments); // eslint-disable-line prefer-rest-params
}

function injectGtm(gtmId) {
  window.dataLayer.push({ 'gtm.start': Date.now(), event: 'gtm.js' });
  const s = document.createElement('script');
  s.async = true;
  s.src = `https://www.googletagmanager.com/gtm.js?id=${gtmId}`;
  document.head.appendChild(s);
}

// GTM (y con él GA4/Clarity/Pixel) se descarga cuando el navegador está ocioso y después del
// evento `load`, para que no compita con el LCP ni con el TBT de quien ya aceptó.
// Desarrollo local: no medir (ensucia GA4, Clarity y Pixel con sesiones de prueba).
const LOCAL_HOSTS = ['localhost', '127.0.0.1', '[::1]'];
// Vistas previas de Firebase (canales de los PR, *.web.app / *.firebaseapp.com): tampoco miden.
const isTestHost = (h) =>
  LOCAL_HOSTS.includes(h) ||
  h.endsWith('.web.app') ||
  h.endsWith('.firebaseapp.com');

function loadGtm(gtmId) {
  if (window.__bisonGtmLoaded || isTestHost(location.hostname)) return;
  window.__bisonGtmLoaded = true;
  const run = () =>
    'requestIdleCallback' in window
      ? requestIdleCallback(() => injectGtm(gtmId), { timeout: 4000 })
      : setTimeout(() => injectGtm(gtmId), 2000);
  if (document.readyState === 'complete') run();
  else window.addEventListener('load', run, { once: true });
}

function clearCookies(patterns) {
  const host = location.hostname;
  const parent = host.split('.').slice(-2).join('.');
  document.cookie.split('; ').forEach((c) => {
    const name = c.split('=')[0];
    if (!patterns.some((re) => re.test(name))) return;
    [host, `.${host}`, `.${parent}`].forEach((domain) => {
      document.cookie = `${name}=; max-age=0; path=/; domain=${domain}`;
    });
  });
}

function applyConsent(consent, gtmId) {
  gtag('consent', 'update', {
    analytics_storage: consent.analytics ? 'granted' : 'denied',
    ad_storage: consent.marketing ? 'granted' : 'denied',
    ad_user_data: consent.marketing ? 'granted' : 'denied',
    ad_personalization: consent.marketing ? 'granted' : 'denied',
  });
  if (consent.analytics || consent.marketing) loadGtm(gtmId);
}

// Distancia (px) desde el borde inferior del viewport a la banda que se sondea.
const BAND_FROM_BOTTOM = 80;

// Mismo criterio que THEMES del Navbar: translúcido sobre el fondo de la sección.
const TONES = {
  light: {
    bg: 'rgba(255,255,255,0.88)',
    border: 'rgba(31,36,34,0.1)',
    eyebrow: 'text.accent',
    text: 'grey.800',
    title: 'grey.900',
    desc: 'grey.700',
  },
  dark: {
    bg: 'rgba(10,15,14,0.82)',
    border: 'rgba(255,255,255,0.12)',
    eyebrow: 'primary.light',
    text: 'rgba(255,255,255,0.85)',
    title: 'common.white',
    desc: 'rgba(255,255,255,0.7)',
  },
};

function ToggleRow({ id, title, desc, checked, disabled, onChange, tone }) {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: '16px',
        py: '12px',
        borderTop: '1px solid',
        borderColor: tone.border,
      }}
    >
      <Box sx={{ flex: 1 }}>
        <Typography
          id={`${id}-label`}
          sx={{ fontSize: '14px', fontWeight: 600, color: tone.title }}
        >
          <T>{title}</T>
        </Typography>
        <Typography
          sx={{ fontSize: '13px', lineHeight: 1.5, color: tone.desc }}
        >
          <T>{desc}</T>
        </Typography>
      </Box>
      <Switch
        checked={checked}
        disabled={disabled}
        onChange={(e) => onChange?.(e.target.checked)}
        slotProps={{ input: { 'aria-labelledby': `${id}-label` } }}
      />
    </Box>
  );
}

export default function ConsentManager({ gtmId }) {
  const t = useTranslation();
  const [open, setOpen] = useState(false);
  const [configuring, setConfiguring] = useState(false);
  const [draft, setDraft] = useState({ analytics: false, marketing: false });
  const pathname = usePathname();
  const [dark, setDark] = useState(pathname === '/');

  useEffect(() => {
    window.dataLayer = window.dataLayer || [];
    gtag('consent', 'default', { ...DENIED, wait_for_update: 500 });
    const stored = readConsent();
    if (stored) {
      applyConsent(stored, gtmId);
      setDraft(stored);
    } else {
      setOpen(true);
    }
  }, [gtmId]);

  useEffect(() => {
    const reopen = () => {
      setDraft(readConsent() || { analytics: false, marketing: false });
      setConfiguring(true);
      setOpen(true);
    };
    window.addEventListener(CONSENT_OPEN_EVENT, reopen);
    return () => window.removeEventListener(CONSENT_OPEN_EVENT, reopen);
  }, []);

  const save = useCallback(
    (next) => {
      const prev = readConsent();
      writeConsent(next);
      setDraft(next);
      setOpen(false);
      setConfiguring(false);
      const withdrawn =
        (prev?.analytics && !next.analytics) ||
        (prev?.marketing && !next.marketing);
      if (withdrawn) {
        if (!next.analytics) clearCookies(ANALYTICS_COOKIES);
        if (!next.marketing) clearCookies(MARKETING_COOKIES);
        applyConsent(next, gtmId);
        if (window.__bisonGtmLoaded) location.reload(); // detiene las etiquetas ya cargadas
        return;
      }
      applyConsent(next, gtmId);
    },
    [gtmId]
  );

  // Igual que el Navbar: una banda de 1px a la altura del banner detecta la sección
  // [data-nav-theme] que queda detrás y el banner adopta su tono.
  useEffect(() => {
    if (!open) return;
    const sections = document.querySelectorAll('[data-nav-theme]');
    if (!sections.length) return;
    const probeY = Math.max(window.innerHeight - BAND_FROM_BOTTOM, 0);
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting)
            setDark(entry.target.dataset.navTheme === 'dark');
        });
      },
      {
        rootMargin: `-${probeY}px 0px -${BAND_FROM_BOTTOM - 1}px 0px`,
        threshold: 0,
      }
    );
    sections.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, [open, pathname]);

  if (!open) return null;

  const tone = dark ? TONES.dark : TONES.light;

  return (
    <Box
      role="dialog"
      aria-label={t('Preferencias de cookies')}
      data-surface={dark ? 'dark' : 'light'}
      sx={{
        position: 'fixed',
        right: { xs: 0, sm: 24 },
        left: { xs: 0, sm: 'auto' },
        bottom: { xs: 0, sm: 24 },
        zIndex: 1300,
        width: { xs: '100%', sm: 480 },
        maxHeight: '90vh',
        overflowY: 'auto',
        p: '20px 22px',
        // En el teléfono el banner toca el borde inferior: deja libre la barra de inicio de iOS.
        pb: { xs: 'calc(20px + env(safe-area-inset-bottom))', sm: '20px' },
        bgcolor: tone.bg,
        backdropFilter: 'saturate(140%) blur(12px)',
        WebkitBackdropFilter: 'saturate(140%) blur(12px)',
        border: '1px solid',
        borderColor: tone.border,
        borderRadius: { xs: '8px 8px 0 0', sm: '8px' },
        transition: 'background-color 0.3s ease, border-color 0.3s ease',
      }}
    >
      <Typography
        sx={{
          fontFamily: MONO_FONT,
          fontSize: '11px',
          letterSpacing: '0.1em',
          textTransform: 'uppercase',
          color: tone.eyebrow,
          fontWeight: 600,
          mb: '6px',
        }}
      >
        <T>Cookies</T>
      </Typography>
      <Typography
        sx={{
          fontSize: '14px',
          lineHeight: 1.55,
          color: tone.text,
          mb: '14px',
        }}
      >
        <T>
          Usamos cookies analíticas y de marketing solo si las aceptas. Las
          necesarias siempre están activas. Más detalle en la
        </T>{' '}
        <Box
          component={Link}
          href="/cookies"
          sx={{
            color: 'text.accent',
            borderBottom: '1px solid',
            borderColor: 'primary.light',
          }}
        >
          <T>Política de Cookies</T>
        </Box>
        .
      </Typography>

      {configuring && (
        <Box sx={{ mb: '14px' }}>
          <ToggleRow
            tone={tone}
            id="ck-nec"
            title={t('Necesarias')}
            desc={t('Recuerdan tu elección y hacen funcionar el sitio.')}
            checked
            disabled
          />
          <ToggleRow
            tone={tone}
            id="ck-ana"
            title={t('Analíticas')}
            desc={t(
              'Google Analytics y Microsoft Clarity: tráfico, mapas de calor y grabaciones de sesión.'
            )}
            checked={draft.analytics}
            onChange={(v) => setDraft((d) => ({ ...d, analytics: v }))}
          />
          <ToggleRow
            tone={tone}
            id="ck-mkt"
            title={t('Marketing')}
            desc={t('Meta Pixel: medición de campañas en Meta Ads.')}
            checked={draft.marketing}
            onChange={(v) => setDraft((d) => ({ ...d, marketing: v }))}
          />
        </Box>
      )}

      {/* Teléfono: botones apilados a lo ancho, «Aceptar todas» primero. Desde 600px, los tres en una fila. */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          gap: '8px',
          '& > *': {
            flex: { xs: '0 0 auto', sm: '1 1 0' },
            width: { xs: '100%', sm: 'auto' },
            minHeight: { xs: 44, sm: 'auto' },
            minWidth: 0,
            whiteSpace: 'nowrap',
            px: '8px',
            fontSize: { xs: '14px', sm: '13px' },
          },
        }}
      >
        <Button
          size="small"
          onClick={() => save({ analytics: true, marketing: true })}
        >
          <T>Aceptar todas</T>
        </Button>
        <Button
          variant="ghost"
          size="small"
          sx={{ color: 'text.accent' }}
          aria-label={t('Rechazar cookies no necesarias')}
          onClick={() => save({ analytics: false, marketing: false })}
        >
          <T>Rechazar</T>
        </Button>
        {configuring ? (
          <Button
            variant="ghost"
            size="small"
            sx={{ color: 'text.accent' }}
            onClick={() => save(draft)}
          >
            <T>Guardar</T>
          </Button>
        ) : (
          <Button
            variant="ghost"
            size="small"
            sx={{ color: 'text.accent' }}
            onClick={() => setConfiguring(true)}
          >
            <T>Configurar</T>
          </Button>
        )}
      </Box>
    </Box>
  );
}
